import { Card, CardContent, CardHeader } from "@/components/ui/card";
import React from "react";

interface OitvCardProps {
  children?: React.ReactNode;
}

/**
 * Componente de card principal da aplicação Oi TV
 * Fornece o container base com estilos específicos da marca
 * 
 * @param props - Propriedades do componente
 * @returns {JSX.Element} Componente React de card
 */
const OitvCard: React.FC<OitvCardProps> = ({ children }) => {
  return (
    <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
      {children}
    </Card>
  );
};

export default OitvCard;


interface OitvCardHeaderProps {
  leftSlot?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Cabeçalho do card com logo Oi TV e slot para ações
 * Inclui logo centralizado e área para botões de navegação
 * 
 * @param props - Propriedades do componente
 * @returns {JSX.Element} Componente React de cabeçalho
 */
export const OitvCardHeader: React.FC<OitvCardHeaderProps> = ({ leftSlot, children }) => {
  return (
    <CardHeader className="p-0 px-6">
      <div className="mb-6 flex items-center justify-center relative">
        {leftSlot && (
          <div className="absolute left-0 flex items-center">{leftSlot}</div>
        )}
        <img 
          src="/icon_oi_tv.svg" 
          alt="Oi TV" 
          className="w-20 h-[57px] object-contain" 
        />
      </div>
      <div className="space-y-2 text-left">{children}</div>
    </CardHeader>
  );
};

/**
 * Componente de título para o card
 * Renderiza um título principal com estilos padronizados
 * 
 * @param props - Propriedades do componente
 * @returns {JSX.Element} Componente React de título
 */
export const OitvCardTitle: React.FC<React.PropsWithChildren> = ({ children }) => (
  <h1 className="text-2xl font-bold">{children}</h1>
);

/**
 * Componente de subtítulo para o card
 * Renderiza um subtítulo com estilos padronizados
 * 
 * @param props - Propriedades do componente
 * @returns {JSX.Element} Componente React de subtítulo
 */
export const OitvCardSubtitle: React.FC<React.PropsWithChildren> = ({ children }) => (
  <p className="text-sm text-gray-600">{children}</p>
);

interface OitvCardContentProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * Componente de conteúdo para o card
 * Fornece o container para o conteúdo principal com espaçamento padronizado
 * 
 * @param props - Propriedades do componente
 * @returns {JSX.Element} Componente React de conteúdo
 */
export const OitvCardContent: React.FC<OitvCardContentProps> = ({ children, className }) => (
  <CardContent className={`pt-4 ${className}`}>
    <div className="flex flex-col space-y-6">
      {children}
    </div>
  </CardContent>
);

